/**
 * Auto-Publish Manager Hook
 *
 * Thin wrapper around useAutoPublishPoller that provides backward-compatible interface.
 *
 * The polling hook handles:
 * - Polling for content with status='completed' AND publish_status='pending'
 * - Processing each item (fetch, generate HTML, call autoPublish)
 * - Toast notifications
 * - Query invalidation
 *
 * This wrapper just maps the poller's return values to the legacy interface.
 */

import { useAutoPublishPoller } from "./useAutoPublishPoller";

interface AutoPublishManagerReturn {
	/** Whether there are items pending auto-publish */
	isProcessing: boolean;
	/** Number of content items currently pending */
	processingCount: number;
	/** Whether the poller is active (backward compat for isConnected) */
	isConnected: boolean;
}

/**
 * Hook that manages auto-publishing via polling.
 *
 * Mount this hook once at the app root level. It delegates all work
 * to useAutoPublishPoller and provides a backward-compatible interface.
 *
 * @example
 * ```tsx
 * function App() {
 *   const { isProcessing, processingCount, isConnected } = useAutoPublishManager();
 *
 *   return (
 *     <div>
 *       {processingCount > 0 && <span>Auto-publishing: {processingCount} pending</span>}
 *       {children}
 *     </div>
 *   );
 * }
 * ```
 */
export function useAutoPublishManager(): AutoPublishManagerReturn {
	const { isPolling, pendingCount } = useAutoPublishPoller();

	return {
		isProcessing: pendingCount > 0,
		processingCount: pendingCount,
		isConnected: isPolling, // backward compat: poller active = "connected"
	};
}

export default useAutoPublishManager;
